import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { IProjectGroupElement } from "src/model/model";
import { IMedia } from "src/model/unityObject";
import { mediaSelector } from "src/features/gallery/state/gallerySlice";
import { getCurrentElements } from "src/features/projectGroups/state/projectGroupsSlice";
import ProjectCard from "../ProjectCard";
import TitleWithTooltip from "src/common/components/TitleWithTooltip/TitleWithTooltip";

const BASE_CARD_WIDTH_REM = 12; // matches original w-[12rem]
const BASE_CARD_HEIGHT_REM = 8; // matches ProjectCard h-32 (8rem)

export const ModulesSelectedForPublication = ({
  selectedProjectIds = [],
}: {
  selectedProjectIds: Array<number | string>;
}) => {
  const { t } = useTranslation();
  const currentElements = useSelector(getCurrentElements);
  const medias = useSelector(mediaSelector);

  const selectedIdSet = useMemo(
    () => new Set(selectedProjectIds.map((id) => Number(id))),
    [selectedProjectIds],
  );

  const selectedCards = useMemo(
    () =>
      currentElements
        .filter((pge: IProjectGroupElement) => selectedIdSet.has(Number(pge.project.id)))
        .map((pge: IProjectGroupElement) => {
          const sourceTbn = pge.project.source_thumbnail as any;
          const mediaId = sourceTbn?.id ? Number(sourceTbn.id) : Number(sourceTbn);
          const s3Url = medias.find((m: IMedia) => Number(m.id) === mediaId)?.s3_url as
            | string
            | undefined;

          return {
            id: String(pge.project.id),
            component: (
              <ProjectCard s3Url={s3Url} projectName={pge.project.name} disabled={false} />
            ),
          };
        }),
    [currentElements, medias, selectedIdSet],
  );

  const count = Math.max(selectedCards.length, 1);

  // measure grid width to scale cards dynamically
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [slotWidth, setSlotWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = gridRef?.current;

    const compute = () => {
      const styles = getComputedStyle(el);
      const gapX = parseFloat(styles.columnGap || "0");
      const totalGap = gapX * (count - 1);
      const containerWidth = el.clientWidth;
      const widthPerSlot = (containerWidth - totalGap) / count;
      setSlotWidth(widthPerSlot);
    };

    compute();
    const ro = new ResizeObserver(() => compute());
    ro.observe(el);
    window.addEventListener("resize", compute);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [count]);

  // helpers to convert rem to px
  const remToPx = (valRem: number) => {
    const root = document.documentElement;
    const fs = parseFloat(getComputedStyle(root).fontSize || "16");
    return valRem * fs;
  };

  const baseWidthPx = remToPx(BASE_CARD_WIDTH_REM);
  const baseHeightPx = remToPx(BASE_CARD_HEIGHT_REM);
  const scale = slotWidth ? Math.min(1, slotWidth / baseWidthPx) : 1;

  // scaled outer border box
  const scaledWidthPx = baseWidthPx * scale;
  const scaledHeightPx = baseHeightPx * scale;

  return (
    <div className="flex flex-col grow w-full h-full justify-between">
      <div className="flex flex-col items-center">
        <TitleWithTooltip
          title={t("pages.projectGroup.publish.confirmationBeforePublish.selectedModules")}
          tooltip={t("tooltip.modulesToBePublishedExplanation")}
          className="font-semibold"
          wrapperClassName="flex items-center justify-center gap-2"
        />

        <div className="w-full flex justify-center">
          <div
            ref={gridRef}
            className="grid gap-1 w-full max-w-5xl"
            style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}
          >
            {selectedCards.map((card) => (
              <div
                key={card.id}
                className="flex items-center justify-center rounded-xl select-none"
              >
                <div
                  className="rounded-xl overflow-hidden border-4 border-green-500"
                  style={{
                    width: scaledWidthPx,
                    height: scaledHeightPx,
                  }}
                >
                  <div
                    className="origin-top-left"
                    style={{
                      width: baseWidthPx,
                      height: baseHeightPx,
                      transform: `scale(${scale})`,
                    }}
                  >
                    {card.component}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModulesSelectedForPublication;
